import { PageState } from './pdfHighlight.utils';

// Bound expensive canvas/text work independently of document length.
const MAX_CONCURRENT_RENDERS = 2;
const MAX_RENDERED_PAGES = 12;

type Job = {
  promise: Promise<void>;
  resolve: () => void;
  cancelled: boolean;
  started?: boolean;
  cancelRender?: () => void;
  cancelText?: () => void;
};

export function createPdfPageRenderer(
  pdf: any,
  TextLayerBuilder: any,
  states: Map<number, PageState>,
  container: HTMLElement,
  scale: number,
  onRendered: (pageNum: number) => void,
  onError: (error: unknown) => void,
) {
  const jobs = new Map<number, Job>();
  const queue: number[] = [];
  const wanted = new Set<number>();
  const retained = new Map<number, () => void>();
  let active = 0;
  let disposed = false;

  const unload = (pageNum: number) => {
    wanted.delete(pageNum);
    const job = jobs.get(pageNum);
    if (job && !job.cancelled) {
      job.cancelled = true;
      job.cancelRender?.();
      job.cancelText?.();
      if (!job.started) {
        const index = queue.indexOf(pageNum);
        if (index !== -1) queue.splice(index, 1);
        jobs.delete(pageNum);
        job.resolve();
      }
    }
    retained.get(pageNum)?.();
    retained.delete(pageNum);
    const state = states.get(pageNum);
    if (!state) return;
    state.placeholder.querySelectorAll('canvas').forEach((canvas) => {
      canvas.width = 0;
      canvas.height = 0;
    });
    state.placeholder.innerHTML = '';
    state.rendered = false;
    state.wrapper = undefined;
    state.textLayerDiv = undefined;
  };

  const run = async (pageNum: number, job: Job) => {
    let pdfPage: any;
    let canvas: HTMLCanvasElement | undefined;
    let textLayer: any;
    const stopped = () => disposed || job.cancelled;
    try {
      pdfPage = await pdf.getPage(pageNum);
      if (stopped()) return;
      const state = states.get(pageNum);
      if (!state) return;
      const viewport = pdfPage.getViewport({ scale });
      // Preserve the reader's position when an estimated page above it changes size.
      const rect = state.placeholder.getBoundingClientRect();
      const oldHeight = parseFloat(state.placeholder.style.height);
      if (rect.bottom <= container.getBoundingClientRect().top) {
        container.scrollTop += viewport.height - oldHeight;
      }
      state.placeholder.style.width = `${viewport.width}px`;
      state.placeholder.style.height = `${viewport.height}px`;
      const dpr = window.devicePixelRatio || 1;
      canvas = document.createElement('canvas');
      canvas.width = viewport.width * dpr;
      canvas.height = viewport.height * dpr;
      canvas.style.cssText = `display:block;width:${viewport.width}px;height:${viewport.height}px;`;
      const ctx = canvas.getContext('2d')!;
      ctx.scale(dpr, dpr);
      const task = pdfPage.render({ canvasContext: ctx, viewport });
      job.cancelRender = () => task.cancel?.();
      await task.promise;
      job.cancelRender = undefined;
      if (stopped()) return;

      textLayer = new TextLayerBuilder({ pdfPage });
      job.cancelText = () => textLayer.cancel?.();
      const textLayerDiv = textLayer.div as HTMLDivElement;
      textLayerDiv.classList.add('pdf-text-layer');
      textLayerDiv.style.cssText = `position:absolute;top:0;left:0;width:${viewport.width}px;height:${viewport.height}px;overflow:hidden;opacity:0;pointer-events:none;user-select:none;`;
      textLayerDiv.style.setProperty('--scale-factor', String(scale));
      const wrapper = document.createElement('div');
      wrapper.style.cssText = `position:relative;width:${viewport.width}px;height:${viewport.height}px;`;
      wrapper.append(canvas, textLayerDiv);
      await textLayer.render(viewport);
      job.cancelText = undefined;
      if (stopped()) return;
      state.placeholder.replaceChildren(wrapper);
      state.rendered = true;
      state.wrapper = wrapper;
      state.textLayerDiv = textLayerDiv;
      retained.set(pageNum, () => {
        textLayer.cancel?.();
        pdfPage.cleanup?.();
      });
      while (retained.size > MAX_RENDERED_PAGES) {
        unload(retained.keys().next().value);
      }
      onRendered(pageNum);
    } catch (error) {
      if (!stopped()) onError(error);
    } finally {
      if (!states.get(pageNum)?.rendered || stopped()) {
        if (canvas) canvas.width = canvas.height = 0;
        textLayer?.cancel?.();
        pdfPage?.cleanup?.();
      }
    }
  };

  let pump: () => void;
  const start = async (pageNum: number, job: Job) => {
    try {
      await run(pageNum, job);
    } finally {
      active--;
      jobs.delete(pageNum);
      job.resolve();
      pump();
    }
  };

  pump = () => {
    while (!disposed && active < MAX_CONCURRENT_RENDERS && queue.length) {
      const pageNum = queue.shift()!;
      const job = jobs.get(pageNum)!;
      if (job.cancelled) {
        jobs.delete(pageNum);
        continue;
      }
      active++;
      job.started = true;
      void start(pageNum, job);
    }
  };

  const render = (pageNum: number): Promise<void> => {
    if (disposed) return Promise.resolve();
    const state = states.get(pageNum);
    if (!state) return Promise.resolve();
    wanted.add(pageNum);
    if (state.rendered) {
      const release = retained.get(pageNum);
      if (release) {
        retained.delete(pageNum);
        retained.set(pageNum, release);
      }
      return Promise.resolve();
    }
    const existing = jobs.get(pageNum);
    if (existing && !existing.cancelled) return existing.promise;
    // A cancelled in-flight job must settle before the page can be rendered again.
    if (existing)
      return existing.promise.then(() => {
        if (wanted.has(pageNum)) return render(pageNum);
      });
    let resolve!: () => void;
    const promise = new Promise<void>((done) => {
      resolve = done;
    });
    jobs.set(pageNum, { promise, resolve, cancelled: false });
    queue.push(pageNum);
    pump();
    return promise;
  };

  return {
    render,
    unload,
    dispose() {
      disposed = true;
      queue.length = 0;
      jobs.forEach((job) => {
        job.cancelled = true;
        job.cancelRender?.();
        job.cancelText?.();
        job.resolve();
      });
      states.forEach((_state, pageNum) => unload(pageNum));
    },
  };
}
